import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UsersPage from "./Users";
import Network from "./Network";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useYacht } from "../../context/YachtContext";

const SENIOR_ROLES = new Set(["owner", "manager", "captain", "first_officer", "viewer"]);

export default function CrewNetwork() {
  const [params, setParams] = useSearchParams();
  const { yachts } = useYacht();

  // "My network" tab only exists for users who hold a senior role
  // (owner / manager / captain) on at least one yacht. Junior crew
  // can't invite anyone, so a personal address book has no purpose
  // for them. The read-only `viewer` (demo) account is also allowed in so
  // the shareable demo can showcase the supplier address book; Network
  // renders read-only for it (all mutation buttons are gated off).
  const canSeeNetwork = useMemo(
    () => (yachts || []).some((y) => SENIOR_ROLES.has(y.my_role)),
    [yachts],
  );

  const wantsNetwork = params.get("view") === "network";
  const wantsTeam = params.get("view") === "team";
  const initial = !canSeeNetwork ? "yacht"
    : wantsNetwork ? "network"
    : wantsTeam ? "team"
    : "yacht";
  const [tab, setTab] = useState(initial);

  // If gating flips after yachts load (or the user just got demoted),
  // bounce them back to the yacht tab and strip the ?view query.
  useEffect(() => {
    if (!canSeeNetwork && (tab === "network" || tab === "team")) {
      setTab("yacht");
      const p = new URLSearchParams(params);
      p.delete("view");
      setParams(p, { replace: true });
    }
  }, [canSeeNetwork, tab, params, setParams]);

  // React to ?view= changes made while mounted (e.g. a supplier row on the
  // Active-yacht tab jumping to the network tab's Info popup).
  useEffect(() => {
    const want = params.get("view");
    if ((want === "network" || want === "team") && canSeeNetwork && tab !== want) setTab(want);
  }, [params, canSeeNetwork]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = (next) => {
    if ((next === "network" || next === "team") && !canSeeNetwork) return;
    setTab(next);
    const p = new URLSearchParams(params);
    if (next === "yacht") p.delete("view");
    else p.set("view", next);
    setParams(p, { replace: true });
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <Tabs value={tab} onValueChange={handle} className="w-full">
        <TabsList
          className={`grid w-full max-w-xl ${canSeeNetwork ? "grid-cols-3" : "grid-cols-1"}`}
          data-testid="crew-network-tabs"
        >
          <TabsTrigger value="yacht" data-testid="tab-active-yacht">Active yacht</TabsTrigger>
          {canSeeNetwork && (
            <TabsTrigger value="team" data-testid="tab-my-team">My team</TabsTrigger>
          )}
          {canSeeNetwork && (
            <TabsTrigger value="network" data-testid="tab-my-network">My network</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="yacht" className="m-0 mt-2 p-0">
          {/* Users page already renders its own header & content */}
          <div className="-m-6 lg:-m-10">
            <UsersPage />
          </div>
        </TabsContent>

        {canSeeNetwork && (
          <TabsContent value="team" className="m-0 mt-2 p-0">
            <div className="-m-6 lg:-m-10">
              <Network section="team" />
            </div>
          </TabsContent>
        )}

        {canSeeNetwork && (
          <TabsContent value="network" className="m-0 mt-2 p-0">
            <div className="-m-6 lg:-m-10">
              <Network section="suppliers" />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
